/**
 * BlogContent — a tiny, dependency-free markdown renderer tuned for the glam
 * AB8 "Press" reading experience. Supports:
 *
 *   # / ## / ###   headings
 *   > quote         pull-quote (pink, oversized)
 *   - / * / 1.      bullet & numbered lists
 *   ![alt](url)     block image (rounded, soft pink frame)
 *   ---             polka-dot section divider
 *   **bold**  *italic*  `code`  [text](url)   inline
 *
 * The very first paragraph gets a decorative pink drop-cap. Admin authors are
 * trusted, but we still render to React elements (never dangerouslySetInnerHTML)
 * so stray HTML can't break the page.
 */

// ─── inline parser: **bold** *italic* `code` [text](url) ─────────────────────
function renderInline(text, keyBase = 'i') {
  const nodes = [];
  let rest = text;
  let k = 0;
  // Order matters: links first, then code, bold, italic.
  const pattern = /(\[([^\]]+)\]\(([^)\s]+)\))|(`([^`]+)`)|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/;
  while (rest.length) {
    const m = rest.match(pattern);
    if (!m) { nodes.push(rest); break; }
    if (m.index > 0) nodes.push(rest.slice(0, m.index));
    const key = `${keyBase}-${k++}`;
    if (m[1]) {
      nodes.push(
        <a key={key} href={m[3]} target={m[3].startsWith('http') ? '_blank' : undefined}
           rel="noopener noreferrer"
           className="text-tea-600 underline decoration-tea-300 decoration-2 underline-offset-2 hover:text-tea-800">
          {m[2]}
        </a>
      );
    } else if (m[4]) {
      nodes.push(<code key={key} className="rounded bg-tea-50 text-tea-700 px-1.5 py-0.5 text-[0.9em] font-mono">{m[5]}</code>);
    } else if (m[6]) {
      nodes.push(<strong key={key} className="font-bold text-gray-900">{m[7]}</strong>);
    } else if (m[8]) {
      nodes.push(<em key={key} className="italic">{m[9]}</em>);
    }
    rest = rest.slice(m.index + m[0].length);
  }
  return nodes;
}

// Polka-dot divider — three pink dots on the line (keyed by caller in the blocks array).
function DotDivider() {
  return (
    <div className="flex items-center justify-center gap-2.5 my-10" aria-hidden="true">
      <span className="w-2.5 h-2.5 rounded-full bg-tea-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-tea-300" />
      <span className="w-2.5 h-2.5 rounded-full bg-tea-400" />
    </div>
  );
}

export default function BlogContent({ body }) {
  if (!body || !body.trim()) {
    return <p className="text-gray-400 italic">This story is still being written…</p>;
  }

  const lines = body.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let firstParaDone = false;

  const flushList = (items, ordered, key) => {
    const Tag = ordered ? 'ol' : 'ul';
    blocks.push(
      <Tag key={key} className={(ordered ? 'list-decimal' : 'list-disc') +
        ' pl-6 my-5 space-y-2 text-gray-700 marker:text-tea-400'}>
        {items.map((it, idx) => <li key={idx} className="leading-relaxed">{renderInline(it, `${key}-${idx}`)}</li>)}
      </Tag>
    );
  };

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();

    if (!line) { i++; continue; }

    // Horizontal rule → polka-dot divider
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) { blocks.push(<DotDivider key={`hr-${i}`} />); i++; continue; }

    // Headings
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const cls = level === 1
        ? 'font-display text-3xl md:text-4xl font-extrabold text-gray-900 mt-10 mb-4 tracking-tight'
        : level === 2
          ? 'font-display text-2xl md:text-3xl font-bold text-gray-900 mt-9 mb-3'
          : 'text-xl font-bold text-tea-700 mt-7 mb-2';
      const Tag = `h${level}`;
      blocks.push(<Tag key={`h-${i}`} className={cls}>{renderInline(h[2], `h-${i}`)}</Tag>);
      i++; continue;
    }

    // Block image: ![alt](url)
    const img = line.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
    if (img) {
      blocks.push(
        <figure key={`img-${i}`} className="my-8">
          <div className="relative rounded-3xl overflow-hidden ring-1 ring-tea-100 shadow-[0_18px_50px_-20px_rgba(219,39,119,0.45)]">
            <img src={img[2]} alt={img[1]} className="w-full object-cover" loading="lazy" />
          </div>
          {img[1] && <figcaption className="mt-2.5 text-center text-xs uppercase tracking-[0.15em] text-gray-400">{img[1]}</figcaption>}
        </figure>
      );
      i++; continue;
    }

    // Blockquote / pull-quote (consume consecutive > lines)
    if (line.startsWith('>')) {
      const quote = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quote.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      blocks.push(
        <blockquote key={`q-${i}`} className="relative my-9 pl-7 pr-4 py-2">
          <span className="absolute left-0 top-0 text-6xl leading-none text-tea-200 font-serif select-none">“</span>
          <p className="font-display text-xl md:text-2xl italic text-gray-800 leading-relaxed">
            {renderInline(quote.join(' '), `q-${i}`)}
          </p>
        </blockquote>
      );
      continue;
    }

    // Lists (consume consecutive list items of the same kind)
    const ulMatch = line.match(/^[-*]\s+(.*)$/);
    const olMatch = line.match(/^\d+\.\s+(.*)$/);
    if (ulMatch || olMatch) {
      const ordered = !!olMatch;
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        const um = t.match(/^[-*]\s+(.*)$/);
        const om = t.match(/^\d+\.\s+(.*)$/);
        if (ordered && om) items.push(om[1]);
        else if (!ordered && um) items.push(um[1]);
        else break;
        i++;
      }
      flushList(items, ordered, `list-${i}`);
      continue;
    }

    // Paragraph (consume until blank line / next block token)
    const para = [];
    while (i < lines.length) {
      const t = lines[i].trim();
      if (!t || /^(#{1,3}\s|>|!\[|[-*]\s|\d+\.\s|-{3,}$|\*{3,}$|_{3,}$)/.test(t)) break;
      para.push(t);
      i++;
    }
    const paraText = para.join(' ');
    if (!firstParaDone) {
      firstParaDone = true;
      blocks.push(
        <p key={`p-${i}`} className="text-gray-700 leading-[1.85] text-lg first-letter:float-left first-letter:font-display first-letter:text-6xl first-letter:leading-[0.8] first-letter:pr-3 first-letter:pt-1 first-letter:text-tea-500 first-letter:font-extrabold">
          {renderInline(paraText, `p-${i}`)}
        </p>
      );
    } else {
      blocks.push(<p key={`p-${i}`} className="text-gray-700 leading-[1.85] text-lg my-5">{renderInline(paraText, `p-${i}`)}</p>);
    }
  }

  return <div className="blog-content">{blocks}</div>;
}
